import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Grid, Table, TableBody, TableCell, TableHead, TableRow, Button,
  Paper, IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  Container, Header, ActionButtons, TableWrapper, TableRowStyled, TableDescription, OpenStatus,
  CloseStatus,
  OnHoldStatus,
  StyledBox
} from './JobListing.styles';
import FilterSortDialog, { JobFilters } from './FilterSortDialog/FilterSortDialog';
import Pagination from '../../../Common/Pagination/pagination';
import StatusCard from '../../../Common/Card/Card';
import { theme } from '../../../../theme';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { deleteJob, exportJobData, getJobs } from '../jobManagemnetApi';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import SearchBar from '../../../Common/Search/SearchBar';
import JobDetailsDialog from './JobDetailsSidebar';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

interface JobData {
  JobNum: number;
  JobTitle: string;
  Department: string;
  Status: string;
  CreatedDate: string;
  CreatedBy: string;
  UpdatedDate: string;
  UpdatedBy: string;
  JobCode: string;
  year_of_experience?: number;
  ctc?: number;
  skills?: string[];
  recruiter?: string;
  [key: string]: any;
  DeletedItem: boolean;
  JobPostingId: string;
}



const JobListing: React.FC = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalJobs, setTotalJobs] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // For delete confirmation dialog
  const [jobData, setJobData] = useState<JobData[]>([]);
  const { tenant } = useParams<{ tenant: string }>();
  const columnsData = ['Job Posting ID', 'Job Title', 'Department', 'Job Status', 'Created Details', 'Updated Details', 'Actions']
  const navigate = useNavigate()
  const [jobNumToDelete, setJobNumToDelete] = useState<number | null>(null);
  const [jobDataAvailable, setJobDataAvailable] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isJobsLoading, setIsJobsLoading] = useState(false)
  const [activeFilter, setActiveFilter] = useState<string | null>(null)
  const [jobsCount, setJobsCount] = useState({ openJobsCount: 0, closeJobsCount: 0, onholdJobsCount: 0 });
  const [jobFilters, setJobFilters] = useState<JobFilters>({
    sortBy: '',
    selectedFilters: [],
    filterText: {},
    jobStatus: '',
  });
  const [searchQuery, setSearchQuery] = useState('')
  const [isJobDetailsOpen, setIsJobDetailsOpen] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);
  const { showSnackbar } = useSnackbar();
  const [createdBy, setCreatedBy] = useState('')
  const [createdDate, setCreatedDate] = useState('')

  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedJobNum, setSelectedJobNum] = useState<string | number | null>(null);
  const [jobTitle, setJobTitle] = useState('')
  const exportRef = React.useRef<HTMLButtonElement | null>(null);

  // Called when user clicks the export icon for a specific job
  const handleExportClick = (event: React.MouseEvent<HTMLElement>, jobNum: string | number, jobTitle: string) => {
    setSelectedJobNum(jobNum);
    setExportAnchorEl(event.currentTarget);
    setJobTitle(jobTitle)
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
    setSelectedJobNum(null);
    setJobTitle('')
  };

  const handleExport = async (jobNum: string | number, format: 'pdf' | 'docx', jobTitle: string) => {
    const payload = {
      JobNum: jobNum,
      format: format,
    };
    await exportJobData(tenant, payload, jobTitle);
    handleExportClose(); // ✅ this was missing
  };


  const hasActiveFilters = (filters: typeof jobFilters): boolean => {
    return (
      filters.sortBy.trim() !== '' ||
      filters.jobStatus.trim() !== '' ||
      filters.selectedFilters.length > 0 ||
      Object.values(filters.filterText || {}).some(val => val?.trim() !== '')
    );
  };

  // Fetch Job Data
  const fetchJobData = async (
    filters = jobFilters,
    pageNum = page,
    pageSize = rowsPerPage,
    jobTitle = searchQuery
  ) => {
    try {
      setIsJobsLoading(true);
      // setLoading(true);

      const payload: any = {
        page: pageNum,
        pageSize: pageSize,
        status: filters.jobStatus,
        sortBy: filters.sortBy || "",
        jobTitle: jobTitle
      };

      if (filters.selectedFilters?.length && filters.filterText) {
        filters.selectedFilters.forEach((filter: string) => {
          const value = filters.filterText[filter]?.trim();
          if (value) {
            payload[filter] = value;
          }
        });
      }


      const result = await getJobs(tenant, payload);

      if (result) {
        setJobData(result.Jobs);
        const isFiltered = hasActiveFilters(filters);
        setJobDataAvailable(result.Jobs.length > 0 || isFiltered || Boolean(jobTitle));

        setJobsCount({
          openJobsCount: result.OpenJobs || 0,
          closeJobsCount: result.CloseJobs || 0,
          onholdJobsCount: result.OnHoldJobs || 0,
        });
        setTotalJobs(result.TotalJobs || 0);
      }
    } catch (error) {
      console.error("Fetch Jobs Error:", error);
      setJobData([]);
    } finally {
      setIsJobsLoading(false);
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchJobData(jobFilters, page, rowsPerPage);
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // 🚫 Skip on initial render
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const updatedFilters = {
        ...jobFilters,
      };
      fetchJobData(updatedFilters, page, rowsPerPage, searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);



  const formatEpochToDate = (epochTime: any) => {
    const date = new Date(epochTime * 1000); // Convert seconds to milliseconds
    return date.toISOString().split('T')[0]; // Format as 'YYYY-MM-DD'
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleDeleteJob = (jobNum: number) => {
    setJobNumToDelete(jobNum); // Set job ID to delete
    setDeleteDialogOpen(true); // Open delete confirmation dialog
  };

  // Confirm deletion
  const confirmDeleteJob = async () => {
    const payLoad = {
      "JobNum": jobNumToDelete
    }
    setDeleteDialogOpen(false); // Close dialog after deletion
    const result = await deleteJob(tenant, payLoad)
    if (result) {
      if (result.Status) {
        fetchJobData(jobFilters, page, rowsPerPage, searchQuery)
        showSnackbar("Job deleted successfully.", "success")
      } else {
        showSnackbar(result.Message || "This job cannot be deleted because it is currently assigned to one or more candidates.", "error")
      }
    }
    setJobNumToDelete(null); // Reset job ID to delete
  };

  const cancelDeleteJob = () => {
    setDeleteDialogOpen(false); // Close delete dialog without deleting
    setJobNumToDelete(null); // Reset job ID to delete
  }




  const handleApplyFilters = (newFilters: {
    sortBy: string;
    selectedFilters: string[];
    filterText: { [key: string]: string };
    jobStatus: string;
  }) => {
    const isFiltered = hasActiveFilters(newFilters);

    if (isFiltered) {
      setJobFilters(newFilters);
      fetchJobData(newFilters, 1, 10);
    }

    handleCloseDialog();
  };

  const handleClearFilterAPI = async () => {
    const clearedFilters = {
      sortBy: '',
      selectedFilters: [],
      filterText: {},
      jobStatus: '',
    };
    setJobFilters(clearedFilters);
    setActiveFilter(''); // Also clear active card filter if applicable
    fetchJobData(clearedFilters, 1, 10);
  }



  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };


  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setPage(1);
  };

  const handleEditJob = (jobNum: number) => {
    navigate(`/${tenant}/Joblisting/create-job`, { state: { jobNum } })
  }
  const handleViewJob = (jobNum: number) => {
    navigate(`/${tenant}/Joblisting/create-job`, { state: { jobNum, ReadOnly: true } })
  }

  const handleFilterByCard = (status: string) => {
    if (activeFilter === status) {
      handleClearFilterByCard(); // Automatically clear if same card is clicked
    } else {
      const updatedFilters = {
        ...jobFilters,
        jobStatus: status,
      };
      setActiveFilter(status);
      setJobFilters(updatedFilters);
      fetchJobData(updatedFilters, 1, 10);
    }
  };

  const handleClearFilterByCard = () => {
    setActiveFilter(null); // Clear the filter
    const updatedFilters = {
      ...jobFilters,
      jobStatus: '',
    };
    setJobFilters(updatedFilters);
    fetchJobData(updatedFilters, 1, 10);
  };

  if (loading) {
    return (
      <Box sx={{
        height: '90vh', // Ensures full height
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center', width: '100%'
      }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!jobDataAvailable) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="90vh"  // Ensures the container takes full viewport height
        textAlign="center"  // Centers the text
        overflow="hidden"  // Prevents scrolling
        sx={{ width: '100%', pt: 0 }}  // Ensures full width
      >
        {/* <i className="fa-solid fa-file-circle-xmark fa-5x" style={{ padding: '0.5vw', marginLeft: '0.5vw', color: theme.palette.text.disabled, marginBottom: 8 }}></i> */}
        <HourglassEmptyIcon style={{ fontSize: 60, width: 60, height: 60,color:theme.palette.primary.main }} />

        <Typography variant='subtitle2'>
            No Job Posts Available!
        </Typography>

        <Typography variant="subtitle3" sx={{ mb: 2 }}>
          You haven't created any jobs yet. Start by creating a job to find the best candidates.
        </Typography>

        <Link
          to={`/${tenant}/Joblisting/create-job`}
          style={{ textDecoration: 'none' }} // Ensures the link doesn't have an underline
        >
          <Button variant="contained" color="primary">
            Create
          </Button>
        </Link>
      </Box>
    )
  }

  const handleOpenJobDetails = async (jobTitle: string, createdBy: string, createdDate: string, jobNum: number) => {
    setJobTitle(jobTitle)
    setCreatedBy(createdBy)
    setCreatedDate(createdDate)
    setIsJobDetailsOpen(true)
    setJobNumToDelete(jobNum)
  }
  return (
    <Box
      sx={{
        marginLeft: '2.3vw',
        marginRight: '1.3vw',
      }}
    >
      <Container >
        <Header>
          <Box>
            <Typography variant="h5">
              List of Jobs
            </Typography>
            <Typography variant="subtitle1" gutterBottom >
              Overview of Open, Closed and On-Hold Positions
            </Typography>
          </Box>
          <Link
            style={{ color: 'primary', height: 'inherit', textDecoration: 'none' }}
            to={`/${tenant}/Joblisting/create-job`}
          >
            <Button
              variant="contained"
              sx={{ whiteSpace: 'nowrap', display: 'flex', gap: '0.8vw' }}
              color="primary"
            >
              <i className="fa-solid fa-plus " /> Create New Job
            </Button>
          </Link>
        </Header>

        <Grid container spacing={2} sx={{ mb: '1vw' }}>
          <Grid item xs={12} sm={4}>
            <StatusCard
              filterOption='Open'
              title="Open"
              count={jobsCount.openJobsCount}
              handleFilterByCard={handleFilterByCard}
              cardfilter={activeFilter === 'Open'} // Highlight active card
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatusCard
              filterOption='Close'
              title="Closed"
              count={jobsCount.closeJobsCount}
              handleFilterByCard={handleFilterByCard}
              cardfilter={activeFilter === 'Close'} // Highlight active card
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatusCard
              filterOption='On-hold'
              title="On-Hold"
              count={jobsCount.onholdJobsCount}
              handleFilterByCard={handleFilterByCard}
              cardfilter={activeFilter === 'On-hold'} // Highlight active card
            />
          </Grid>

        </Grid>

        <ActionButtons>
          <SearchBar
            placeholder='Search by Job Title'
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
          <Button
            variant="outlined"
            sx={{ color: theme.palette.primary.main, borderColor: theme.palette.primary.main }}
            onClick={handleOpenDialog}
          >
            Filter & Sort
          </Button>
        </ActionButtons>

        <FilterSortDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onApply={handleApplyFilters}
          filters={jobFilters}
          handleClearFilterAPI={handleClearFilterAPI}
        />

        <TableWrapper>

          <Box sx={{ overflowX: 'auto' }}>
            <Table >
              <TableHead>
                <TableRow>
                  {columnsData.map((column) => (
                    <TableCell
                      key={column}
                      sx={{
                        color: theme.palette.text.secondary,
                        padding: '8px 12px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '13.89vw',
                        minWidth: '6.94vw',
                        textAlign: ['actions', 'job status'].includes(column.toLowerCase()) ? 'center' : 'left',
                      }}
                    >
                      <Typography variant='caption'>
                        {column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>

              </TableHead>

              <TableBody >
                {isJobsLoading ? (
                  <TableRow>
                    <TableCell colSpan={columnsData.length} style={{ textAlign: 'center' }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : jobData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columnsData.length} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      No Job Data Available
                    </TableCell>
                  </TableRow>
                ) : (
                  jobData.map((data, index) => (
                    <TableRowStyled key={data.JobNum} iseven={(index % 2 === 0).toString()} >
                      <TableDescription>{data.JobPostingId ? data.JobPostingId : '-'}</TableDescription>
                      <TableDescription onClick={() => handleOpenJobDetails(data.JobTitle, data.CreatedBy, data.CreatedDate, data.JobNum)} sx={{
                        overflowWrap: 'break-word',
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                      title={data.JobTitle} 
                      >{data.JobTitle}</TableDescription>
                      <TableDescription>{data.Department}</TableDescription>
                      <TableDescription sx={{
                        paddingLeft: 0,
                        paddingRight: 0,
                        textAlign: 'center'
                      }}>
                        {data.Status === 'Open' ? (
                          <OpenStatus>• {data.Status}</OpenStatus>
                        ) : data.Status === 'Close' ? (
                          <CloseStatus>• {data.Status}</CloseStatus>
                        ) : (
                          <OnHoldStatus>• {data.Status}</OnHoldStatus>
                        )}
                      </TableDescription>
                      <TableDescription>
                        <StyledBox >
                          <Typography variant='body2'>{data.CreatedBy}</Typography>
                          <Typography variant='body2'>{formatEpochToDate(data.CreatedDate)}</Typography>
                        </StyledBox>

                      </TableDescription>
                      <TableDescription>
                        <StyledBox >
                          <Typography variant='body2'>{data.UpdatedBy || '-'}</Typography>
                          <Typography variant='body2'>{data.UpdatedDate ? formatEpochToDate(data.UpdatedDate) : '-'}</Typography>
                        </StyledBox>
                      </TableDescription>
                      <TableDescription sx={{
                        paddingLeft: 0,
                        paddingRight: 0,
                        textAlign: 'center'
                      }}>
                        <Box sx={{ display: 'flex', gap: '8px', color: theme.palette.primary.main,alignItems:'center',justifyContent:'center' }}>
                          <IconButton onClick={() => { handleViewJob(data.JobNum) }} sx={{ color: theme.palette.primary.main }}>
                            <i className="fa-solid fa-eye fa-xs"></i>
                          </IconButton>
                          <IconButton onClick={() => { handleEditJob(data.JobNum) }} sx={{ color: theme.palette.primary.main }}>
                            <i className="fa-solid fa-pen fa-xs"></i>
                          </IconButton>
                          <IconButton onClick={() => { handleDeleteJob(data.JobNum) }} sx={{ color: theme.palette.primary.main }}>
                            <i className="fa-solid fa-trash fa-xs"></i>
                          </IconButton>
                          <IconButton
                            onClick={(e) => handleExportClick(e, data.JobNum, data.JobTitle)}
                            sx={{ color: theme.palette.primary.main }}
                            ref={exportRef}
                          >
                            <i className="fa-solid fa-file-export fa-xs"></i>
                          </IconButton>

                          <Menu
                            anchorEl={exportAnchorEl}
                            open={Boolean(exportAnchorEl)}
                            onClose={handleExportClose}
                          >
                            <MenuItem
                              onClick={() => handleExport(selectedJobNum!, 'pdf', jobTitle)}>Export as PDF</MenuItem>
                            <MenuItem
                              onClick={() => handleExport(selectedJobNum!, 'docx', jobTitle)}>Export as Word</MenuItem>
                          </Menu>

                        </Box>
                      </TableDescription>
                    </TableRowStyled>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>

          {
            totalJobs > 0 && (
              <Box sx={{ mt: '2.5vh', display: 'flex', mb: 2, mx: 2, justifyContent: 'center' }}>
                <Pagination
                  totalRows={totalJobs}
                  rowsPerPage={rowsPerPage}
                  currentPage={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </Box>
            )
          }

        </TableWrapper>

        <JobDetailsDialog
          open={isJobDetailsOpen}
          onClose={() => setIsJobDetailsOpen(false)}
          jobTitle={jobTitle}
          createdBy={createdBy}
          createdDate={createdDate}
          jobNum={jobNumToDelete}
        />

        <Dialog
          open={deleteDialogOpen}
          onClose={cancelDeleteJob}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this job posting? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDeleteJob} sx={{ color: theme.palette.text.secondary }}>
              Cancel
            </Button>
            <Button onClick={confirmDeleteJob}
              variant="contained"
              sx={{
                bgcolor: theme.palette.error.main,
                '&:hover': {
                  bgcolor: theme.palette.error.dark,
                },
              }}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>


    </Box>
  );
};

export default JobListing;